// Query key factory for authentication-related queries
export const authKeys = {
  all: ["auth"] as const,
  user: () => [...authKeys.all, "user"] as const,
  profile: () => [...authKeys.all, "profile"] as const,
  permissions: () => [...authKeys.all, "permissions"] as const,

  // Specific user queries
  userById: (id: string) => [...authKeys.user(), id] as const,
  userProfile: (id: string) => [...authKeys.profile(), id] as const,
};

// Query key factory for user-related queries
export const userKeys = {
  all: ["users"] as const,
  lists: () => [...userKeys.all, "list"] as const,
  list: (filters?: object) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, "detail"] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  stats: () => [...userKeys.all, "stats"] as const,
};
export const pathsKeys = {
  all: ["paths"] as const,
  lists: () => [...pathsKeys.all, "list"] as const,
  list: (filters?: object) => [...pathsKeys.lists(), filters] as const,
  details: () => [...pathsKeys.all, "detail"] as const,
  detail: (id: string) => [...pathsKeys.details(), id] as const,
  stats: () => [...pathsKeys.all, "stats"] as const,
};

export const coursesKeys = {
  all: ["courses"] as const,
  lists: () => [...coursesKeys.all, "list"] as const,
  list: (filters?: object) => [...coursesKeys.lists(), filters] as const,
  details: () => [...coursesKeys.all, "detail"] as const,
  detail: (id: string) => [...coursesKeys.details(), id] as const,
  /** React Query mutation key — shared by all course media upload call sites */
  mediaUpload: (slug: string) =>
    [...coursesKeys.detail(slug), "media-upload"] as const,
  byPath: (params: object) => [...coursesKeys.all, "by-path", params] as const,
  stats: () => [...coursesKeys.all, "stats"] as const,
};

export const tracksKeys = {
  all: ["tracks"] as const,
  lists: () => [...tracksKeys.all, "list"] as const,
  list: (filters?: object) => [...tracksKeys.lists(), filters] as const,
  details: () => [...tracksKeys.all, "detail"] as const,
  detail: (id: string) => [...tracksKeys.details(), id] as const,
  stats: () => [...tracksKeys.all, "stats"] as const,
};

export const reviewsKeys = {
  all: ["reviews"] as const,
  lists: () => [...reviewsKeys.all, "list"] as const,
  list: (filters?: object) => [...reviewsKeys.lists(), filters] as const,
  details: () => [...reviewsKeys.all, "detail"] as const,
  detail: (id: string) => [...reviewsKeys.details(), id] as const,
};

export const faqsKeys = {
  all: ["faqs"] as const,
  categories: () => [...faqsKeys.all, "categories"] as const,
  questions: () => [...faqsKeys.all, "questions"] as const,
  questionList: (filters?: object) => [...faqsKeys.questions(), filters] as const,
};

export const testimonialsKeys = {
  all: ["testimonials"] as const,
  lists: () => [...testimonialsKeys.all, "list"] as const,
  list: (filters?: object) => [...testimonialsKeys.lists(), filters] as const,
  details: () => [...testimonialsKeys.all, "detail"] as const,
  detail: (id: string) => [...testimonialsKeys.details(), id] as const,
};

export const couponsKeys = {
  all: ["coupons"] as const,
  lists: () => [...couponsKeys.all, "list"] as const,
  list: (filters?: object) => [...couponsKeys.lists(), filters] as const,
  byCourse: (idOrSlug: string) =>
    [...couponsKeys.all, "course", idOrSlug] as const,
  details: () => [...couponsKeys.all, "detail"] as const,
  detail: (id: string) => [...couponsKeys.details(), id] as const,
};

export const curriculumKeys = {
  all: ["curriculum"] as const,
  sections: (courseSlug: string) =>
    [...curriculumKeys.all, courseSlug, "sections"] as const,
  sectionDetail: (courseSlug: string, id: string) =>
    [...curriculumKeys.sections(courseSlug), id] as const,
  lectureVideo: (lectureId: string) =>
    [...curriculumKeys.all, "lectureVideo", lectureId] as const,
};

export const ordersKeys = {
  all: ["orders"] as const,
  lists: () => [...ordersKeys.all, "list"] as const,
  list: (filters?: object) => [...ordersKeys.lists(), filters] as const,
  details: () => [...ordersKeys.all, "detail"] as const,
  detail: (id: string) => [...ordersKeys.details(), id] as const,
};

export const paymentsKeys = {
  all: ["payments"] as const,
  lists: () => [...paymentsKeys.all, "list"] as const,
  list: (filters?: object) => [...paymentsKeys.lists(), filters] as const,
  details: () => [...paymentsKeys.all, "detail"] as const,
  detail: (id: string) => [...paymentsKeys.details(), id] as const,
};

export const refundsKeys = {
  all: ["refunds"] as const,
  lists: () => [...refundsKeys.all, "list"] as const,
  list: (filters?: object) => [...refundsKeys.lists(), filters] as const,
};

export const consultationKeys = {
  all: ["consultations"] as const,
  availability: () => [...consultationKeys.all, "availability"] as const,
  availabilityList: (params?: object) =>
    [...consultationKeys.all, "availability-list", params] as const,
  availabilityDetail: (id: string) =>
    [...consultationKeys.all, "availability-detail", id] as const,
  /** GET /availability?from=&to= — computed slots in date range */
  availabilityWindow: (from: string, to: string) =>
    [...consultationKeys.all, "availability-window", { from, to }] as const,
  instructorAvailability: (id: string) =>
    [...consultationKeys.availability(), id] as const,
  schedules: (from: string, to: string) =>
    [...consultationKeys.all, "schedules", { from, to }] as const,
  googleCalendarStatus: () =>
    [...consultationKeys.all, "google-calendar-status"] as const,
};

export const analyticsKeys = {
  all: ["analytics"] as const,
  usersOverview: () => [...analyticsKeys.all, "users", "overview"] as const,
  usersMostActiveMonthly: (params?: object) =>
    [...analyticsKeys.all, "users", "most-active-monthly", params] as const,
  usersNewUsersMonthly: (params?: object) =>
    [...analyticsKeys.all, "users", "new-users-monthly", params] as const,
  usersDailyActive: (params: object) =>
    [...analyticsKeys.all, "users", "daily-active", params] as const,
  coursesMostJoinedMonthly: (params?: object) =>
    [...analyticsKeys.all, "courses", "most-joined-monthly", params] as const,
  coursesProgressOverview: () =>
    [...analyticsKeys.all, "courses", "progress-overview"] as const,
  coursesTopJoined: (params?: object) =>
    [...analyticsKeys.all, "courses", "top-joined", params] as const,
  lessonsMostWatched: (params?: object) =>
    [...analyticsKeys.all, "lessons", "most-watched", params] as const,
};

export const auditLogKeys = {
  all: ["audit-logs"] as const,
  lists: () => [...auditLogKeys.all, "list"] as const,
  list: (filters?: object) => [...auditLogKeys.lists(), filters] as const,
  details: () => [...auditLogKeys.all, "detail"] as const,
  detail: (id: string) => [...auditLogKeys.details(), id] as const,
};

export const backupKeys = {
  all: ["backups"] as const,
  lists: () => [...backupKeys.all, "list"] as const,
  list: (filters?: object) => [...backupKeys.lists(), filters] as const,
  schedule: () => [...backupKeys.all, "schedule"] as const,
};

export const contentKeys = {
  all: ["content"] as const,
  lists: () => [...contentKeys.all, "list"] as const,
  list: (filters?: object) => [...contentKeys.lists(), filters] as const,
  detail: (contentType: string, id: string) =>
    [...contentKeys.all, contentType, id] as const,
  versions: (contentType: string, id: string) =>
    [...contentKeys.all, contentType, id, "versions"] as const,
  versionDetail: (contentType: string, id: string, versionId: string) =>
    [...contentKeys.all, contentType, id, "versions", versionId] as const,
};

export const departmentKeys = {
  all: ["departments"] as const,
  lists: () => [...departmentKeys.all, "list"] as const,
  list: (filters?: object) => [...departmentKeys.lists(), filters] as const,
  tree: () => [...departmentKeys.all, "tree"] as const,
  detail: (id: string) => [...departmentKeys.all, id] as const,
};

export const pageKeys = {
  all: ["pages"] as const,
  lists: () => [...pageKeys.all, "list"] as const,
  list: (filters?: object) => [...pageKeys.lists(), filters] as const,
  detail: (id: string) => [...pageKeys.all, id] as const,
};

export const documentKeys = {
  all: ["documents"] as const,
  lists: () => [...documentKeys.all, "list"] as const,
  list: (filters?: object) => [...documentKeys.lists(), filters] as const,
  detail: (id: string) => [...documentKeys.all, id] as const,
  scope: () => [...documentKeys.all, "scope"] as const,
};

export const documentLibraryKeys = {
  all: ["document-libraries"] as const,
  lists: () => [...documentLibraryKeys.all, "list"] as const,
  list: (filters?: object) => [...documentLibraryKeys.lists(), filters] as const,
};

export const notificationKeys = {
  all: ["notifications"] as const,
  preferences: () => [...notificationKeys.all, "preferences"] as const,
  inbox: (filters?: object) =>
    [...notificationKeys.all, "inbox", filters ?? {}] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
  detail: (id: string) => [...notificationKeys.all, "detail", id] as const,
};

export const dashboardKeys = {
  all: ["dashboard"] as const,
  health: () => [...dashboardKeys.all, "health"] as const,
  recentLogins: () => [...dashboardKeys.all, "recent-logins"] as const,
  recentAudit: () => [...dashboardKeys.all, "recent-audit"] as const,
};

export const reportKeys = {
  all: ["reports"] as const,
  content: (filters?: object) =>
    [...reportKeys.all, "content", filters] as const,
  users: (filters?: object) => [...reportKeys.all, "users", filters] as const,
  userActivityLogins: (filters?: object) =>
    [...reportKeys.all, "user-activity", "logins", filters] as const,
  userActivityAudit: (filters?: object) =>
    [...reportKeys.all, "user-activity", "audit", filters] as const,
  scope: () => [...reportKeys.all, "scope"] as const,
  websiteAnalytics: (filters?: object) =>
    [...reportKeys.all, "website-analytics", filters] as const,
  websiteAnalyticsConfig: () =>
    [...reportKeys.all, "website-analytics", "config"] as const,
};

export const settingsKeys = {
  all: ["settings"] as const,
  root: () => [...settingsKeys.all, "root"] as const,
};

export const permissionKeys = {
  all: ["permissions"] as const,
  matrix: (filters?: object) => [...permissionKeys.all, "matrix", filters] as const,
  me: () => [...permissionKeys.all, "me"] as const,
  user: (id: string) => [...permissionKeys.all, "user", id] as const,
};

export const roleKeys = {
  all: ["roles"] as const,
  list: () => [...roleKeys.all, "list"] as const,
};

// General query key factory patterns
export const queryKeys = {
  auth: authKeys,
  permissions: permissionKeys,
  roles: roleKeys,
  users: userKeys,
  paths: pathsKeys,
  courses: coursesKeys,
  tracks: tracksKeys,
  reviews: reviewsKeys,
  faqs: faqsKeys,
  testimonials: testimonialsKeys,
  coupons: couponsKeys,
  curriculum: curriculumKeys,
  orders: ordersKeys,
  payments: paymentsKeys,
  refunds: refundsKeys,
  consultations: consultationKeys,
  analytics: analyticsKeys,
  auditLogs: auditLogKeys,
  backups: backupKeys,
  content: contentKeys,
  departments: departmentKeys,
  pages: pageKeys,
  documents: documentKeys,
  documentLibraries: documentLibraryKeys,
  notifications: notificationKeys,
  reports: reportKeys,
  dashboard: dashboardKeys,
  settings: settingsKeys,
};
